import React from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Flex, Text, HStack, IconButton,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';

/**
 * PageShell — renders children either as a full-page embedded view or inside a Modal.
 *
 * Props:
 *   fullPage  — if true, renders inline (no modal)
 *   onBack    — callback for the back button (full-page mode)
 *   isOpen    — modal open state (modal mode)
 *   onClose   — modal close callback (modal mode)
 *   title     — header text
 *   size      — modal size (ignored in full-page mode)
 *   headerRight — optional JSX rendered on the right of the header
 */
export default function PageShell({ fullPage, onBack, isOpen, onClose, title, size, headerRight, children }) {
  if (fullPage) {
    return (
      <Box h="100%" minH={0} display="flex" flexDirection="column" bg="white" overflow="hidden">
        <Flex bg="white" borderBottom="1px" borderColor="gray.200" px={4} h="44px" minH="44px"
          align="center" justify="space-between" flexShrink={0}>
          <HStack spacing={2}>
            <IconButton icon={<FiArrowLeft />} size="sm" variant="ghost" onClick={onBack} aria-label="Back to Dashboard" />
            <Text fontSize="14px" fontWeight="700" color="gray.700">{title}</Text>
          </HStack>
          {headerRight || null}
        </Flex>
        {/* minH=0 is essential: without it a flex child's min-height is
            content-height and overflowY:auto never engages — the classic
            "page won't scroll" flexbox trap. */}
        <Box flex={1} minH={0} overflowY="auto" p={4}>
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size || 'full'} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="95vh" mx={size === 'full' ? 4 : undefined}>
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8} align="center">
            <Text>{title}</Text>
            {headerRight || null}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>{children}</ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
